import React, { useState, useEffect } from 'react';
import './usertools-css/VitaminIroncalculator.css';
import '../../styles/components/ainutritionlandingpage.css';

const ironRdaTable = {
    // mg/day (heme-equivalent base)
    '0-6m': { rda: 0.27, isAI: true },
    '7-12m': { rda: 11, isAI: true }, // Note: High AI due to rapid growth/stores
    '1-3y': { rda: 7 },
    '4-8y': { rda: 10 },
    '9-13y': { rda: 8 },
    '14-18_male': { rda: 11 },
    '14-18_female': { rda: 15 },
    '19-50_male': { rda: 8 },
    '19-50_female': { rda: 18 },
    '51+_male': { rda: 8 },
    '51+_female': { rda: 8 }, // Post-menopausal assume same as male
    'pregnant': { rda: 27 }, // Applies regardless of age during pregnancy
    'lactating': { rda: 9 },  // Applies regardless of age during lactation
    'vegetarian_multiplier': 1.8,
};

const ironULTable = { // mg/day
    infant: 40, // 0-12 months
    child_adult: 45 // 1 year+
};

const menstrualFlowBonusMg = {
    none: 0,
    light: 1,
    moderate: 2,
    heavy: 4,
};

// Simplified food intake estimates (mg/day) - VERY ROUGH
// Needs refinement based on HEME vs NON-HEME if possible later
const ironFoodIntakeEstimates = {
    low: 5,    // Assumes low meat/fortified intake
    medium: 12, // Assumes mixed diet with some meat/fortified
    high: 20,  // Assumes high meat/fortified intake
};

// Absorption Modifiers based on blueprint's suggestion
const absorptionModifiers = {
    vitC_enhancer: 0.50, // +50%
    tea_coffee_inhibitor: -0.40, // -40%
    calcium_inhibitor: -0.30, // -30%
    gi_malabsorption_penalty: 0.7, // Multiplier (e.g., 30% reduction) - applied to food intake
};

// --- Component ---
function IronCalculator() {
    // --- State for User Inputs ---
    const [age, setAge] = useState('');
    const [sex, setSex] = useState(''); // 'male', 'female'
    const [isPregnant, setIsPregnant] = useState(false);
    const [isLactating, setIsLactating] = useState(false);
    const [dietPattern, setDietPattern] = useState(''); // 'omnivore', 'pescatarian', 'lacto-ovo', 'vegan'
    const [menstrualFlow, setMenstrualFlow] = useState('none'); // 'none', 'light', 'moderate', 'heavy'
    const [ironFoodIntakeLevel, setIronFoodIntakeLevel] = useState(''); // 'low', 'medium', 'high'

    // Meal Modifiers State
    const [mealModifiers, setMealModifiers] = useState({
        vitCWithMeal: false,
        teaCoffeeWithMeal: false,
        calciumWithMeal: false,
    });

    // Health Factors State (Iron relevant)
    const [healthFactorsIron, setHealthFactorsIron] = useState({
        cond_gi_malabsorption_iron: false, // Renamed to avoid conflict if merged later
        cond_ckd: false, // Chronic Kidney Disease
        cond_hemochromatosis: false, // Iron overload disorder
        // Add blood donor / endurance athlete flags if needed
    });

    // Supplement State
    const [takesSupplementIron, setTakesSupplementIron] = useState(false);
    const [supplementDoseMgIron, setSupplementDoseMgIron] = useState(''); // Dose in elemental mg
    const [supplementFrequencyIron, setSupplementFrequencyIron] = useState(''); // 'daily', 'weekly', etc.

    // --- State for Results ---
    const [calculationResult, setCalculationResult] = useState(null); // Will hold results object
    const [showResults, setShowResults] = useState(false);

    // --- Input Handlers ---
    const handleHealthFactorChange = (event) => {
        const { name, checked } = event.target;
        setHealthFactorsIron(prev => ({ ...prev, [name]: checked }));
    };

    const handleMealModifierChange = (event) => {
        const { name, checked } = event.target;
        setMealModifiers(prev => ({ ...prev, [name]: checked }));
    };

    // --- Derived State ---
    const ageNum = parseInt(age, 10);
    const isFemale = sex === 'female';
    const isMenstruatingAge = isFemale && ageNum >= 12 && ageNum <= 50; // Approximate age range

    // Disable pregnancy/lactation/menstruation based on sex and age
    useEffect(() => {
        if (sex === 'male' || ageNum < 12 || ageNum > 55) { // Adjust age range
            setIsPregnant(false);
            setIsLactating(false);
            setMenstrualFlow('none');
        }
        if (!isMenstruatingAge && sex === 'female') {
            setMenstrualFlow('none'); // Ensure non-menstruating females have 'none' selected
        }
    }, [sex, ageNum, isMenstruatingAge]);


    // --- Calculation Logic ---
    const calculateIronIntake = (e) => {
        e.preventDefault();
        setShowResults(false);
        setCalculationResult(null);

        let explanation = [];
        let safetyFlags = [];
        let targetRda = 0;

        // 1. Determine Base RDA & Adjustments
        let baseRdaKey = '';
        let isVegetarian = dietPattern === 'lacto-ovo' || dietPattern === 'vegan';

        if (!isNaN(ageNum)) {
            if (isPregnant) {
                baseRdaKey = 'pregnant';
            } else if (isLactating) {
                baseRdaKey = 'lactating';
            } else if (ageNum >= 0 && ageNum < 0.5) {
                baseRdaKey = '0-6m';
            } else if (ageNum >= 0.5 && ageNum < 1) {
                baseRdaKey = '7-12m';
            } else if (ageNum >= 1 && ageNum <= 3) {
                baseRdaKey = '1-3y';
            } else if (ageNum >= 4 && ageNum <= 8) {
                baseRdaKey = '4-8y';
            } else if (ageNum >= 9 && ageNum <= 13) {
                baseRdaKey = '9-13y';
            } else if (ageNum >= 14 && ageNum <= 18) {
                baseRdaKey = sex === 'male' ? '14-18_male' : '14-18_female';
            } else if (ageNum >= 19 && ageNum <= 50) {
                baseRdaKey = sex === 'male' ? '19-50_male' : '19-50_female';
            } else if (ageNum >= 51) {
                baseRdaKey = sex === 'male' ? '51+_male' : '51+_female';
            }

            if (ironRdaTable[baseRdaKey]) {
                targetRda = ironRdaTable[baseRdaKey].rda;
                explanation.push(`Base ${ironRdaTable[baseRdaKey].isAI ? 'AI' : 'RDA'} (${baseRdaKey.replace(/_/g, ' ')}): ${targetRda} mg/day.`);

                // Vegetarian Multiplier (Apply before menstrual bonus)
                if (isVegetarian) {
                    const originalRda = targetRda; // Store the original RDA
                    targetRda *= ironRdaTable.vegetarian_multiplier;
                    explanation.push(`Base RDA: ${originalRda} mg/day.`);
                    explanation.push(`x${ironRdaTable.vegetarian_multiplier} multiplier applied for ${dietPattern} diet = ${targetRda.toFixed(1)} mg/day.`);
                }

                // Menstrual Flow Bonus (Apply after vegetarian multiplier if needed)
                if (isMenstruatingAge && menstrualFlow !== 'none') {
                    const bonus = menstrualFlowBonusMg[menstrualFlow] || 0;
                    if (bonus > 0) {
                        targetRda += bonus;
                        explanation.push(`+${bonus} mg/day added for ${menstrualFlow} menstrual flow.`);
                    }
                }
                explanation.push(`-> Final Target Intake: ${targetRda.toFixed(1)} mg/day.`);

            } else {
                explanation.push("Could not determine base RDA for the inputs provided.");
                targetRda = ironRdaTable['19-50_male'].rda; // Fallback
            }

        } else {
            explanation.push("Please enter a valid age.");
            targetRda = ironRdaTable['19-50_male'].rda; // Default fallback
        }

        // 2. Estimate Raw Food Intake (Highly Simplified)
        let rawFoodIntake = ironFoodIntakeEstimates[ironFoodIntakeLevel] || 0;
        if (ironFoodIntakeLevel) {
            explanation.push(`Estimated baseline RAW food iron intake (${ironFoodIntakeLevel} iron foods): ~${rawFoodIntake} mg/day. (Accuracy limited without specific food details).`);
        } else {
            explanation.push("Select typical iron food intake level to estimate food contribution.");
        }

        // 3. Calculate Effective Food Intake applying Absorption Modifiers
        let absorptionFactor = 1.0; // Start with base absorption assumption for the estimate
        let modifierExplanation = [];
        if (mealModifiers.vitCWithMeal) {
            absorptionFactor += absorptionModifiers.vitC_enhancer;
            modifierExplanation.push('+Vit C');
        }
        if (mealModifiers.teaCoffeeWithMeal) {
            absorptionFactor += absorptionModifiers.tea_coffee_inhibitor; // Add the negative value
            modifierExplanation.push('-Tea/Coffee');
        }
        if (mealModifiers.calciumWithMeal) {
            absorptionFactor += absorptionModifiers.calcium_inhibitor; // Add the negative value
            modifierExplanation.push('-Calcium');
        }

        // Clamp the factor to prevent unrealistic values (e.g., 5% to 150% of baseline)
        absorptionFactor = Math.max(0.05, Math.min(1.5, absorptionFactor));

        if (modifierExplanation.length > 0) {
            explanation.push(`Absorption factor based on meal modifiers (${modifierExplanation.join(', ')}): ${absorptionFactor.toFixed(2)}x.`);
        } else {
            explanation.push(`No specific meal modifiers selected; using base absorption estimate (factor = 1.0x).`);
        }

        let effectiveFoodIntake = rawFoodIntake * absorptionFactor;

        // Apply GI Malabsorption Penalty AFTER meal modifiers
        if (healthFactorsIron.cond_gi_malabsorption_iron) {
            const penaltyFactor = absorptionModifiers.gi_malabsorption_penalty;
            effectiveFoodIntake *= penaltyFactor;
            explanation.push(`Further reduced effective food intake by ~${((1 - penaltyFactor) * 100).toFixed(0)}% due to GI malabsorption factor. Effective Food Intake: ~${effectiveFoodIntake.toFixed(1)} mg/day.`);
            safetyFlags.push({ type: 'info', text: 'GI malabsorption factor applied; actual impact varies. Consult provider.' });
        } else {
            explanation.push(`Estimated EFFECTIVE food iron intake (after absorption factors): ~${effectiveFoodIntake.toFixed(1)} mg/day.`);
        }


        // 4. Calculate Supplement Intake (Assumed highly bioavailable, added AFTER food absorption adjustments)
        let supplementIntake = 0;
        const dose = parseFloat(supplementDoseMgIron);
        if (takesSupplementIron && !isNaN(dose) && dose > 0 && supplementFrequencyIron) {
            let dailyFactor = 0;
            switch (supplementFrequencyIron) {
                case 'daily': dailyFactor = 1; break;
                case 'alternate_day': dailyFactor = 0.5; break;
                case '3x_week': dailyFactor = 3 / 7; break;
                case 'weekly': dailyFactor = 1 / 7; break;
                default: dailyFactor = 0; break;
            }
            supplementIntake = dose * dailyFactor;
            explanation.push(`Supplement contribution (elemental iron, assumed well-absorbed): ${dose} mg taken ${supplementFrequencyIron.replace('_', ' ')} ≈ ${supplementIntake.toFixed(1)} mg/day average.`);
        }

        // 5. Calculate Total Effective Intake & Gap
        const effectiveTotalIntake = effectiveFoodIntake + supplementIntake;
        explanation.push(`==> Estimated TOTAL EFFECTIVE iron intake (Food + Supplement): ${effectiveTotalIntake.toFixed(1)} mg/day.`);

        const gap = targetRda - effectiveTotalIntake;
        explanation.push(`Gap compared to Target RDA: ${gap > 0 ? `${gap.toFixed(1)} mg/day deficit` : `${Math.abs(gap).toFixed(1)} mg/day surplus (or adequate)`}.`);

        // 6. Safety Checks
        const rawTotalIntakeForUL = rawFoodIntake + (takesSupplementIron && !isNaN(dose) && dose > 0 ? (dose * (supplementFrequencyIron === 'daily' ? 1 : 0.5)) : 0); // Use raw food + daily/alt-day supplement dose for worst-case UL check
        const currentUL = ageNum < 1 ? ironULTable.infant : ironULTable.child_adult;
        explanation.push(`Tolerable Upper Intake Level (UL) for age ${age}: ${currentUL} mg/day.`);

        if (rawTotalIntakeForUL > currentUL) {
            safetyFlags.push({ type: 'warning', text: `Estimated total daily intake (~${rawTotalIntakeForUL.toFixed(0)} mg from food + common supplement freq) may exceed the Tolerable Upper Intake Level (UL) of ${currentUL} mg/day. High iron intake can cause GI upset.` });
        }
        if (healthFactorsIron.cond_hemochromatosis) {
            safetyFlags.push({ type: 'severe', text: 'Hemochromatosis selected. AVOID iron supplements unless specifically prescribed by your doctor. Discuss dietary iron limits.' });
            if (rawTotalIntakeForUL > 15) { // Example threshold for extra caution
                safetyFlags.push({ type: 'warning', text: 'Estimated iron intake seems high for someone with hemochromatosis. Review diet with your provider.' });
            }
        }
        if (healthFactorsIron.cond_ckd) {
            safetyFlags.push({ type: 'info', text: 'CKD selected. Iron needs and supplementation should be managed by your nephrologist/healthcare team.' });
        }
        if (effectiveTotalIntake < targetRda * 0.5 && targetRda > 5) { // If intake is less than half the target (and target isn't tiny)
            safetyFlags.push({ type: 'warning', text: 'Estimated effective intake is significantly below target. Risk of iron deficiency symptoms (fatigue, pallor, etc.). Consider discussing with your doctor.' });
        }


        // 7. Generate Recommendations (Simplified Placeholder)
        let recommendations = [];
        if (gap > 0) {
            recommendations.push(`Recommendation: Your estimated effective intake is ~${gap.toFixed(1)} mg below your target.`);
            recommendations.push("Increase intake of iron-rich foods:");
            if (!isVegetarian) recommendations.push("- Heme sources (better absorbed): Red meat, poultry (dark meat), fish, clams.");
            recommendations.push("- Non-heme sources: Lentils, beans, tofu, spinach, fortified cereals/breads.");
            recommendations.push("Boost non-heme absorption: Eat Vitamin C-rich foods (citrus, berries, peppers) with iron sources.");
            recommendations.push("Avoid inhibitors with iron-rich meals/supplements: Tea, coffee, high-calcium foods/supplements (take separately).");

            if (gap >= 8 && !takesSupplementIron) { // Example logic for supplement suggestion
                recommendations.push("A supplement may be needed. Common doses are 18-30 mg elemental iron daily or alternate days. Ferrous bisglycinate is often better tolerated than ferrous sulfate. Discuss with your doctor before starting.");
            } else if (takesSupplementIron && gap > 3) { // If already supplementing but still short
                recommendations.push("Review your current supplement dose/timing and dietary strategies. Ensure you're maximizing absorption. Consult your provider.");
            }
        } else {
            recommendations.push("Recommendation: Your estimated effective intake appears adequate based on these simplified inputs.");
            recommendations.push("Continue consuming a balanced diet with iron-rich foods. Maintain good absorption habits (Vit C pairing, inhibitor timing).");
        }
        if (isVegetarian) {
            recommendations.push("As a vegetarian/vegan, pay close attention to non-heme iron sources and absorption enhancers (Vitamin C) to meet your higher target.");
        }
        if (isMenstruatingAge && menstrualFlow === 'heavy') {
            recommendations.push("With heavy menstrual flow, consistently meeting your higher iron target is crucial to prevent depletion.");
        }
        recommendations.push("Consult your healthcare provider for personalized advice, especially if experiencing symptoms of deficiency, managing health conditions, or before starting/changing supplements. Blood tests (ferritin, Hb) are needed for accurate status assessment.");


        // Set results after a short delay
        setTimeout(() => {
            setCalculationResult({
                rda: targetRda.toFixed(1),
                rawFoodIntake: rawFoodIntake.toFixed(1),
                absorptionFactor: absorptionFactor.toFixed(2),
                effectiveFoodIntake: effectiveFoodIntake.toFixed(1),
                supplementIntake: supplementIntake.toFixed(1),
                effectiveTotalIntake: effectiveTotalIntake.toFixed(1),
                gap: gap.toFixed(1),
                ul: currentUL,
                explanation: explanation.join('\n'),
                recommendations: recommendations,
                safetyFlags: safetyFlags
            });
            setShowResults(true);
        }, 300);
    };

    // --- JSX Structure ---
    return (
        // Add a specific class for Iron styling
        <div className="ironCalculator"> {/* <<< UPDATE CLASS */}
            <h1>
                <span>
                    <span role="img" aria-label="iron intake calculator">
                        🩸
                    </span>{' '}
                    Iron Intake Calculator
                </span>
                <span className="ai-promo-inline">
                    <strong>Get Personalized Health Insights:</strong>{' '}
                    Check your iron status alongside other key nutrients with a tailored plan using a quick{' '}
                    <a
                        href="https://purehealthnexus.com/ai-nutrition-vitamins-minerals"
                        className="ai-promo-link"
                    >
                        AI scan
                    </a>.
                </span>
            </h1>

            <p className="important-note">
                <strong>Disclaimer:</strong> This tool provides a ROUGH ESTIMATE based on general guidelines and simplified inputs (especially for food intake and absorption). It is NOT a substitute for professional medical advice, diagnosis, or treatment. Iron absorption is highly variable. Always consult your physician or qualified health provider regarding your iron status and needs, especially if you have symptoms (fatigue, pallor), risk factors (heavy periods, vegetarian diet, GI conditions), are pregnant, or considering supplements. Blood tests (ferritin, hemoglobin) are necessary to accurately assess iron levels. Do NOT self-treat suspected deficiency or take high-dose iron without medical guidance.
            </p>

            {/* --- Input Form --- */}
            <form onSubmit={calculateIronIntake}> {/* <<< UPDATE HANDLER */}
                <h2>🧾 Your Information</h2>

                {/* Demographics */}
                <div className="form-section">
                    <label>
                        Age (Years):
                        <input type="number" value={age} onChange={(e) => setAge(e.target.value)} required min="0" max="120" placeholder="e.g., 30" />
                    </label>
                    <label>
                        Sex Assigned at Birth:
                        <select value={sex} onChange={(e) => setSex(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </select>
                    </label>
                    {/* Conditional Inputs for Females */}
                    {isFemale && (
                        <>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isPregnant} onChange={(e) => setIsPregnant(e.target.checked)} disabled={isLactating || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Pregnant? (High Need: ~27mg)
                            </label>
                            <label className="checkbox-label">
                                <input type="checkbox" checked={isLactating} onChange={(e) => setIsLactating(e.target.checked)} disabled={isPregnant || !isFemale || ageNum < 12 || ageNum > 55} />
                                Currently Lactating? (Need: ~9mg)
                            </label>
                            {isMenstruatingAge && !isPregnant && !isLactating && (
                                <label>
                                    Typical Menstrual Flow:
                                    <select value={menstrualFlow} onChange={(e) => setMenstrualFlow(e.target.value)} required={isMenstruatingAge}>
                                        <option value="none">None / Not Applicable</option>
                                        <option value="light">Light (+1mg)</option>
                                        <option value="moderate">Moderate (+2mg)</option>
                                        <option value="heavy">Heavy (+4mg)</option>
                                    </select>
                                    <small>Increases daily iron needs.</small>
                                </label>
                            )}
                        </>
                    )}
                </div>

                {/* Diet Pattern */}
                <div className="form-section">
                    <h3>🥗 Dietary Pattern & Intake</h3>
                    <label>
                        Primary Diet Pattern:
                        <select value={dietPattern} onChange={(e) => setDietPattern(e.target.value)} required>
                            <option value="">-- Select --</option>
                            <option value="omnivore">Omnivore (Includes red meat, poultry, fish)</option>
                            <option value="pescatarian">Pescatarian (Includes fish; no other meat)</option>
                            <option value="lacto-ovo">Lacto-Ovo Vegetarian (No meat/fish; needs 1.8x RDA)</option>
                            <option value="vegan">Vegan (No animal products; needs 1.8x RDA)</option>
                        </select>
                        <small>Vegetarian/Vegan diets require ~1.8 times more iron due to lower non-heme absorption.</small>
                    </label>
                    <label>
                        Typical Daily Iron-Rich Food Intake:
                        <select value={ironFoodIntakeLevel} onChange={(e) => setIronFoodIntakeLevel(e.target.value)} required>
                            <option value="">-- Select Estimate --</option>
                            <option value="low">Low (Few servings of meat, beans, fortified foods)</option>
                            <option value="medium">Medium (Mixed intake, some servings daily)</option>
                            <option value="high">High (Regular meat/fish/legume/fortified cereal intake)</option>
                        </select>
                        <small>Rough estimate - specific foods vary greatly!</small>
                    </label>
                </div>

                {/* Meal Modifiers for Absorption */}
                <div className="form-section">
                    <h3>🍽️ Meal Habits Affecting Absorption</h3>
                    <fieldset>
                        <legend>Check if these typically apply to your iron-rich meals or supplements:</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="vitCWithMeal" checked={mealModifiers.vitCWithMeal} onChange={handleMealModifierChange} />
                            Include Vitamin C source? (e.g., citrus, berries, peppers) (+50% Abs.)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="teaCoffeeWithMeal" checked={mealModifiers.teaCoffeeWithMeal} onChange={handleMealModifierChange} />
                            Drink Tea/Coffee with or within 1hr? (-40% Abs.)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="calciumWithMeal" checked={mealModifiers.calciumWithMeal} onChange={handleMealModifierChange} />
                            Take High Calcium food/supplement (&gt;300mg) at the same time? (-30% Abs.)
                        </label>
                    </fieldset>
                </div>

                {/* Health Factors (Iron Relevant) */}
                <div className="form-section">
                    <h3>🩺 Health Factors & Conditions</h3>
                    <fieldset>
                        <legend>Check any that apply (may affect needs or safety):</legend>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_gi_malabsorption_iron" checked={healthFactorsIron.cond_gi_malabsorption_iron} onChange={handleHealthFactorChange} />
                            Condition affecting nutrient absorption? (Crohn's, Celiac, Bariatric Surgery)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_ckd" checked={healthFactorsIron.cond_ckd} onChange={handleHealthFactorChange} />
                            Chronic Kidney Disease (CKD)? (Needs medical management)
                        </label>
                        <label className="checkbox-label indented">
                            <input type="checkbox" name="cond_hemochromatosis" checked={healthFactorsIron.cond_hemochromatosis} onChange={handleHealthFactorChange} />
                            Hereditary Hemochromatosis? (Iron overload risk - AVOID supplements unless prescribed)
                        </label>
                        {/* Add Blood Donor / Athlete checkboxes here if desired */}
                    </fieldset>
                </div>

                {/* Supplement Intake */}
                <div className="form-section">
                    <h3>💊 Iron Supplement Use</h3>
                    <label className="checkbox-label">
                        <input type="checkbox" checked={takesSupplementIron} onChange={(e) => setTakesSupplementIron(e.target.checked)} />
                        Do you currently take an Iron supplement?
                    </label>
                    {takesSupplementIron && (
                        <div className='supplement-details'>
                            <label>
                                Dose per intake (ELEMENTAL Iron, mg): {/* <<< Specify Elemental */}
                                <input type="number" value={supplementDoseMgIron} onChange={(e) => setSupplementDoseMgIron(e.target.value)} required={takesSupplementIron} min="1" placeholder="e.g., 18 or 65" />
                                <small>Check supplement label for 'elemental iron'.</small>
                            </label>
                            <label>
                                Frequency:
                                <select value={supplementFrequencyIron} onChange={(e) => setSupplementFrequencyIron(e.target.value)} required={takesSupplementIron}>
                                    <option value="">-- Select --</option>
                                    <option value="daily">Daily</option>
                                    <option value="alternate_day">Alternate Days</option>
                                    <option value="3x_week">3 times per week</option>
                                    <option value="weekly">Once per week</option>
                                    <option value="as_needed">As needed / Occasionally</option>
                                </select>
                                <small>Alternate day dosing may improve absorption/tolerance.</small>
                            </label>
                        </div>
                    )}
                </div>

                <button type="submit">Calculate Iron Intake</button> {/* <<< UPDATE BUTTON TEXT */}
            </form>

            {/* --- Results Display --- */}
            {showResults && calculationResult && (
                <div className="results-section">
                    <h2>📊 Estimated Iron Status</h2>
                    {/* Safety Flags Display */}
                    {calculationResult.safetyFlags.length > 0 && (
                        <div className="safety-alerts">
                            <h4>⚠️ Safety Alerts & Considerations</h4>
                            <ul>
                                {calculationResult.safetyFlags.map((flag, index) => (
                                    <li key={index} className={`alert-${flag.type}`}>
                                        <strong>{flag.type.toUpperCase()}:</strong> {flag.text}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="results-grid">
                        {/* Update result items for Iron */}
                        <div className="result-item">
                            <span className="result-label">Target RDA/AI:</span>
                            <span className="result-value">{calculationResult.rda} mg/day</span>
                            <small className='result-note'>(Adjusted for age, sex, life stage, diet, flow)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Est. Effective Food Intake:</span>
                            <span className="result-value">{calculationResult.effectiveFoodIntake} mg/day</span>
                            <small className='result-note'>(After absorption factors)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Avg. Supplement Intake:</span>
                            <span className="result-value">{calculationResult.supplementIntake} mg/day</span>
                            <small className='result-note'>(Elemental iron, assumed well-absorbed)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Total Effective Intake:</span>
                            <span className="result-value significant">{calculationResult.effectiveTotalIntake} mg/day</span>
                            <small className='result-note'>(Food + Supplement)</small>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Intake Status vs Target:</span>
                            {/* Using gap value for status */}
                            <span className={`result-value status-${calculationResult.gap > 5 ? 'low' : calculationResult.gap > 1 ? 'borderline' : 'adequate'}`}> {/* Adjusted thresholds for Iron */}
                                {calculationResult.gap > 5 ? `Likely Deficit (~${calculationResult.gap} mg)`
                                    : calculationResult.gap > 1 ? `Potentially Borderline (~${calculationResult.gap} mg deficit)`
                                        : 'Likely Adequate/Surplus'}
                            </span>
                        </div>
                        <div className="result-item">
                            <span className="result-label">Tolerable Upper Limit (UL):</span>
                            <span className="result-value">{calculationResult.ul} mg/day</span>
                            <small className='result-note'>(Max safe daily intake from all sources)</small>
                        </div>
                    </div>

                    <h3>📝 Explanation & Recommendations</h3>
                    <pre className="explanation-box">{calculationResult.explanation}</pre>

                    <div className='recommendations-box'>
                        <h4>Recommendations Summary:</h4>
                        <ul>
                            {calculationResult.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                            ))}
                        </ul>
                        {/* Placeholder for Food/Supplement Links */}
                        <p>
                            <i>Detailed food suggestions (e.g., 75g beef ≈ 2mg heme, ½ cup lentils ≈ 3.3mg non-heme) and specific supplement links (e.g., 18mg ferrous bisglycinate) would appear here in a full implementation.</i>
                        </p>
                        <a href="#iron-education" className="scroll-link">Learn more about Iron below</a> {/* <<< UPDATE LINK HASH */}
                    </div>

                    <p className="important-note">
                        <strong>Crucial Reminder:</strong> This estimate is based on averages and simplified inputs. Individual needs, absorption, and iron stores vary greatly. This tool CANNOT diagnose iron deficiency or overload. Consult your healthcare provider for personalized guidance and necessary blood tests (ferritin, hemoglobin, etc.) if you have symptoms, risk factors, or health conditions.
                    </p>
                </div>
            )}

            {/* --- Educational Content (Iron specific) --- */}
            <div id="iron-education" className="educational-section"> {/* <<< UPDATE ID */}
                <h2>Understanding Iron</h2> {/* <<< UPDATE TITLE */}

                {/* Replace ALL content below with Iron specific info */}
                <div className="content-block">
                    <h3>What is Iron and Why is it Important?</h3>
                    <p>
                        Iron is an essential mineral vital for numerous bodily functions. Its primary role is in oxygen transport: it's a key component of hemoglobin in red blood cells (carrying oxygen from lungs to tissues) and myoglobin in muscles (storing oxygen). Iron is also critical for:
                    </p>
                    <ul>
                        <li>Energy production (electron transport chain).</li>
                        <li>DNA synthesis.</li>
                        <li>Immune function.</li>
                        <li>Neurotransmitter synthesis.</li>
                        <li>Growth and development.</li>
                    </ul>
                    <p>About 70% of the body's iron is found in hemoglobin.</p>
                </div>

                <div className="content-block">
                    <h3>How is Iron Absorbed? Heme vs. Non-Heme</h3>
                    <ul>
                        <li><strong>Heme Iron:</strong> Found in animal flesh (meat, poultry, fish). It's absorbed relatively efficiently (25-30%) via a specific transporter (HCP1) and is less affected by other dietary factors.</li>
                        <li><strong>Non-Heme Iron:</strong> Found in plant foods (legumes, grains, vegetables), dairy, eggs, and iron-fortified foods. Its absorption is much lower and highly variable (1-10%). It must be converted to the ferrous (Fe²⁺) form for absorption via the DMT1 transporter.</li>
                        <li><strong>Absorption Regulation:</strong> The body tightly regulates iron absorption based on existing stores. When stores are low, absorption efficiency increases; when stores are high, it decreases to prevent overload.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Factors Affecting Non-Heme Iron Absorption</h3>
                    <ul>
                        <li><strong>Enhancers:</strong>
                            <ul>
                                <li><strong>Vitamin C (Ascorbic Acid):</strong> Potently enhances non-heme absorption by reducing ferric (Fe³⁺) to ferrous (Fe²⁺) iron and counteracting inhibitors. Consuming ~50mg or more with a meal can significantly boost uptake.</li>
                                <li><strong>Meat/Fish/Poultry Factor (MFP):</strong> The presence of heme iron itself seems to enhance non-heme absorption in the same meal.</li>
                            </ul>
                        </li>
                        <li><strong>Inhibitors:</strong>
                            <ul>
                                <li><strong>Phytates:</strong> Found in whole grains, legumes, nuts, and seeds. Bind strongly to non-heme iron, reducing absorption. Soaking, sprouting, or fermenting can reduce phytate levels.</li>
                                <li><strong>Polyphenols:</strong> Compounds in tea, coffee, red wine, and some vegetables can inhibit absorption. Consume these beverages separately from iron-rich meals (e.g., wait 1 hour).</li>
                                <li><strong>Calcium:</strong> High amounts (e.g., &gt;300mg from dairy or supplements) taken with a meal can compete with iron for absorption.</li>                                     <li><strong>Soy Protein:</strong> Some components in soy products can inhibit non-heme iron absorption.</li>
                            </ul>
                        </li>
                    </ul>
                </div>


                <div className="content-block">
                    <h3>Recommended Intakes (RDA) & Sources</h3>
                    <ul>
                        <li><strong>RDA:</strong> Varies significantly by age, sex, and life stage (see calculator target). Notably higher for menstruating women (18 mg/day) and during pregnancy (27 mg/day).</li>
                        <li><strong>Vegetarians/Vegans:</strong> Need ~1.8 times the RDA due to relying solely on less bioavailable non-heme iron.</li>
                        <li><strong>Rich Food Sources:</strong>
                            <ul>
                                <li>Heme: Red meat (beef, lamb), liver, oysters, clams, mussels, poultry (dark meat), fish.</li>
                                <li>Non-Heme: Lentils, beans (kidney, black, pinto), chickpeas, tofu, spinach, fortified breakfast cereals, enriched breads, pumpkin seeds, molasses.</li>
                            </ul>
                        </li>
                        <li><strong>Supplements:</strong> Available in various forms (ferrous sulfate, gluconate, fumarate; ferrous bisglycinate; heme iron polypeptides). Doses typically range from 18mg to 65mg+ *elemental* iron. Ferrous bisglycinate is often better tolerated (less GI upset). Take supplements as advised by a doctor, often on an empty stomach or with Vitamin C, away from inhibitors.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Who is at Risk for Deficiency?</h3>
                    <ul>
                        <li><strong>Menstruating Women:</strong> Especially those with heavy periods, due to regular blood loss.</li>
                        <li><strong>Pregnant Women:</strong> Increased blood volume and fetal needs dramatically raise requirements.</li>
                        <li><strong>Infants and Young Children (6mo-3y):</strong> Rapid growth, transition from milk, often low intake of iron-rich foods.</li>
                        <li><strong>Adolescents:</strong> Growth spurts increase needs.</li>
                        <li><strong>Vegetarians and Vegans:</strong> Due to lower bioavailability of non-heme iron.</li>
                        <li><strong>Frequent Blood Donors:</strong> Each donation removes significant iron.</li>
                        <li><strong>Endurance Athletes:</strong> Increased losses through sweat, GI bleeding, and red blood cell turnover (hemolysis).</li>
                        <li><strong>Individuals with GI Disorders:</strong> Conditions like celiac disease, IBD (Crohn's, ulcerative colitis), or post-bariatric surgery impair absorption or cause blood loss.</li>
                        <li><strong>People with Chronic Kidney Disease (CKD):</strong> Often require iron supplementation due to blood loss during dialysis and issues with erythropoietin (EPO).</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Symptoms of Iron Deficiency & Anemia</h3>
                    <p>Iron deficiency develops in stages, from depleted stores (low ferritin) to iron-deficient erythropoiesis (low transferrin saturation) to full iron deficiency anemia (low hemoglobin). Symptoms vary but commonly include:</p>
                    <ul>
                        <li>Fatigue, weakness, lack of energy.</li>
                        <li>Pale skin (pallor).</li>
                        <li>Shortness of breath, especially with exertion.</li>
                        <li>Headaches, dizziness.</li>
                        <li>Cold hands and feet.</li>
                        <li>Brittle nails, hair loss.</li>
                        <li>Sore tongue (glossitis), cracks at corners of the mouth (angular stomatitis).</li>
                        <li>Poor appetite (especially in children).</li>
                        <li>Pica (craving non-food items like ice or dirt).</li>
                        <li>Restless legs syndrome.</li>
                        <li>Poor concentration, reduced work/school performance.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Toxicity, UL & Hemochromatosis</h3>
                    <ul>
                        <li><strong>UL:</strong> Tolerable Upper Intake Level is 40 mg/day for infants (&lt;1yr) and 45 mg/day for children and adults (from food + supplements).</li>                            <li><strong>Acute Toxicity:</strong> Accidental overdose (especially in children from supplements) can be dangerous, causing severe GI distress, organ damage, and can be fatal. Keep supplements secure.</li>
                        <li><strong>Chronic Excess / Hemochromatosis:</strong> Hereditary hemochromatosis is a genetic disorder causing excessive iron absorption and accumulation in organs (liver, heart, pancreas), leading to damage. Individuals with this condition must avoid iron supplements and often need treatment (phlebotomy) to remove excess iron. High dietary iron may also need management.</li>
                        <li><strong>GI Side Effects:</strong> Even doses below the UL can cause constipation, nausea, abdominal pain, or diarrhea, especially from ferrous sulfate supplements.</li>
                    </ul>
                </div>

                <div className="content-block">
                    <h3>Testing for Iron Status</h3>
                    <ul>
                        <li><strong>Complete Blood Count (CBC):</strong> Measures Hemoglobin (Hb) and Hematocrit (Hct) to detect anemia. Mean Corpuscular Volume (MCV) may be low in iron deficiency anemia.</li>
                        <li><strong>Serum Ferritin:</strong> Reflects body iron stores. Low ferritin (&lt;15-30 µg/L) is the earliest indicator of deficiency, but it's an acute phase reactant (can be falsely elevated by inflammation/infection).</li>
                        <li><strong>Transferrin Saturation (TSAT):</strong> Percentage of iron-binding sites on transferrin (the main iron transport protein) that are occupied. Low TSAT (&lt;16-20%) indicates insufficient iron supply for red blood cell production.</li>
                        <li><strong>Serum Iron:</strong> Measures iron circulating bound to transferrin; less reliable due to diurnal variation.</li>
                        <li>Other tests (e.g., soluble transferrin receptor - sTfR) may be used in complex cases.</li>
                    </ul>
                    <p>A combination of tests is usually needed for accurate assessment. Consult your doctor.</p>
                </div>

                {/* --- Sources Section (Iron specific) --- */}
                <div className="content-block">
                    <h3>Sources</h3>
                    <p className="sources"> {/* Ensure .sources class has font-size: 8px; */}
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Iron Fact Sheet for Health Professionals*. Retrieved [April-14-2025], from [Insert ODS Prof URL for Iron] <br />
                        National Institutes of Health, Office of Dietary Supplements. (n.d.). *Iron Fact Sheet for Consumers*. Retrieved [April-14-2025], from [Insert ODS Consumer URL for Iron] <br />
                        National Academies of Sciences, Engineering, and Medicine. (2001). *Dietary Reference Intakes for Vitamin A, Vitamin K, Arsenic, Boron, Chromium, Copper, Iodine, Iron, Manganese, Molybdenum, Nickel, Silicon, Vanadium, and Zinc*. The National Academies Press. https://doi.org/10.17226/10026 <br />
                        Sharp, P. (2010). Intestinal iron absorption: Regulation by dietary & systemic factors. *Int J Vitam Nutr Res*, *80*(4-5), 231-42. (Referencing DMT1/HCP1) <br />
                        Hallberg, L., Brune, M., & Rossander, L. (1989). Iron absorption in man: ascorbic acid and dose-dependent inhibition by phytates. *Am J Clin Nutr*, *49*(1), 140-4. (Referencing Vit C & Phytate) <br />
                        Hurrell, R. F. (2010). Influence of vegetable protein sources on trace element and mineral bioavailability. *J Nutr*, *133*(9), 2973S-7S. (Referencing Soy/Phytate) <br />
                        Morck, T. A., Lynch, S. R., & Cook, J. D. (1983). Inhibition of food iron absorption by coffee. *Am J Clin Nutr*, *37*(3), 416-20. (Referencing Coffee) <br />
                        Disler, P. B., Lynch, S. R., Charlton, R. W., Torrance, J. D., Bothwell, T. H., Walker, R. B., & Mayet, F. (1975). The effect of tea on iron absorption. *Gut*, *16*(3), 193–200. (Referencing Tea) <br />
                        Cook, J. D., Dassenko, S. A., & Whittaker, P. (1991). Calcium supplementation: effect on iron absorption. *Am J Clin Nutr*, *53*(1), 106-11. (Referencing Calcium) <br />
                        Institute of Medicine (US) Committee on Nutritional Status During Pregnancy and Lactation. (1990). *Nutrition During Pregnancy*. National Academies Press (US). (Pregnancy needs) <br />
                        USDA FoodData Central. (n.d.). Retrieved [April-14-2025], from [Insert FoodData Central URL]
                    </p>
                    {/* Remember to add URLs and Retrieval Dates */}
                </div>
                {/* --- END OF SOURCES SECTION --- */}

            </div> {/* End educational-section */}
        </div> // End ironCalculator
    );
}

export default IronCalculator;